import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSearchParams } from "react-router-dom";
//import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import OverviewSkel from "../../../../helpers/OverviewSkel";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
  isLoading?: boolean;
  // Changes whenever the brand filter changes; used to reset pagination.
  filterKey?: string;
}

// Query param that persists the current page (1-based) so a refresh keeps the
// user on the same page of this subtab.
const PAGE_PARAM = "negativeBalancePage";

type Order = "asc" | "desc";

// Returns a comparable primitive (number | string) for a cell, or null when
// empty. Numeric strings sort numerically, everything else as lowercased text.
const getComparableValue = (key: string, row: any): number | string | null => {
  const value = row[key];
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return value;
  const str = String(value).trim();
  if (str !== "" && !Number.isNaN(Number(str))) return Number(str);
  return str.toLowerCase();
};

const readPageFromParams = (searchParams: URLSearchParams) => {
  const parsed = parseInt(searchParams.get(PAGE_PARAM) ?? "", 10);
  return Number.isNaN(parsed) || parsed < 1 ? 0 : parsed - 1;
};

const NegativeBalanceTable = ({
  tableHeadings,
  rows,
  isLoading,
  filterKey,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = React.useState(() =>
    readPageFromParams(searchParams),
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<string>("");
  const headCells = Object.entries(tableHeadings);

  // Keep the URL in sync whenever the page changes (pagination, sort or
  // rows-per-page resets), so it survives a refresh.
  React.useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page > 0) next.set(PAGE_PARAM, String(page + 1));
        else next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reset to the first page when the brand filter changes, but skip the initial
  // mount so a refresh can still restore the page from the URL.
  const isFirstFilterRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (key: string) => {
    const isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
    setPage(0);
  };

  const sortedRows = React.useMemo(() => {
    if (!orderBy) return rows;
    return [...rows].sort((a, b) => {
      const aValue = getComparableValue(orderBy, a);
      const bValue = getComparableValue(orderBy, b);
      // Empty values always sort to the bottom.
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, order, orderBy]);

  return (
    <>
      <TableContainer className="">
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleRequestSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <OverviewSkel
              columnCount={headCells.length}
              rowCount={rowsPerPage}
            />
          ) : rows.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  No Data
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <React.Fragment key={row.cron_id}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {headCells.map(([key], cellIndex) => (
                        <TableCell key={cellIndex} align="left">
                          {key === "brand_name" ? (
                            <> {row[key] ? row[key] : "-"}</>
                          ) : key === "brand_id" ? (
                            <> {row[key] ? row[key] : "-"}</>
                          ) : key === "calculated_total_income" ? (
                            <>
                              <Box display={"flex"} alignItems={"center"}>
                                <AttachMoneyIcon
                                  sx={{
                                    fontSize: "14px",
                                  }}
                                />
                                {row[key] ? row[key] : "-"}{" "}
                              </Box>
                            </>
                          ) : key === "last_month_balance" ? (
                            <>
                              <Box display={"flex"} alignItems={"center"}>
                                <AttachMoneyIcon
                                  sx={{
                                    fontSize: "14px",
                                  }}
                                />
                                {row[key] ? row[key] : "-"}{" "}
                              </Box>
                            </>
                          ) : key === "total_income" ? (
                            <>
                              <Box display={"flex"} alignItems={"center"}>
                                <AttachMoneyIcon
                                  sx={{
                                    fontSize: "14px",
                                  }}
                                />
                                {row[key] ? row[key] : "-"}{" "}
                              </Box>
                            </>
                          ) : key === "cpa_income" ? (
                            <>
                              <Box display={"flex"} alignItems={"center"}>
                                <AttachMoneyIcon
                                  sx={{
                                    fontSize: "14px",
                                  }}
                                />
                                {row[key] ? row[key] : "-"}{" "}
                              </Box>
                            </>
                          ) : key === "rs_income" ? (
                            <>
                              <Box display={"flex"} alignItems={"center"}>
                                <AttachMoneyIcon
                                  sx={{
                                    fontSize: "14px",
                                  }}
                                />
                                {row[key] ? row[key] : "-"}{" "}
                              </Box>
                            </>
                          ) : key === "click_out" ? (
                            <>{row[key] ? row[key] : "-"} </>
                          ) : key === "regs" ? (
                            <>{row[key] ? row[key] : "-"} </>
                          ) : key === "qftd" ? (
                            <>{row[key] ? row[key] : "-"} </>
                          ) : key === "ftd" ? (
                            <>{row[key] ? row[key] : "-"}</>
                          ) : row[key] ? (
                            row[key]
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginLeft: "15px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(rows?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingRight: "16px",
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default NegativeBalanceTable;
